import React, { useState } from "react";
import { useShares } from "@/context/SharesContext";
import { useAuth } from "@/context/AuthContext";
import { Share2, Check, X } from "lucide-react";

// Lets a participant flag a reflection or AI synthesis for the coach's attention,
// with an optional short note. Renders nothing for non-participants.
export default function ShareButton({ entryKey, label, getValue, kind = "reflection", testid }) {
  const { user } = useAuth();
  const { sharedKeys, share, unshare } = useShares() || {};
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "participant" || !share) return null;

  const isShared = sharedKeys?.has(entryKey);
  const value = getValue ? String(getValue() || "") : "";
  const hasContent = value.trim().length > 0;

  const doShare = async () => {
    setBusy(true);
    try {
      await share(entryKey, label, value, note, kind);
      setOpen(false);
      setNote("");
    } catch (e) {
      console.error("Failed to share:", e);
    } finally {
      setBusy(false);
    }
  };

  const doUnshare = async () => {
    setBusy(true);
    try { await unshare(entryKey); } catch (e) { console.error(e); } finally { setBusy(false); }
  };

  if (isShared) {
    return (
      <span className="inline-flex items-center gap-2 text-xs">
        <span data-testid={testid ? `${testid}-shared` : undefined} className="inline-flex items-center gap-1.5 rounded-sm border border-brand-sage/40 bg-brand-sage/10 px-2.5 py-1 text-brand-sage">
          <Check size={13} /> Shared with coach
        </span>
        <button data-testid={testid ? `${testid}-unshare` : undefined} onClick={doUnshare} disabled={busy} className="text-stone-400 hover:text-[#9E4A41] transition-colors">
          Unshare
        </button>
      </span>
    );
  }

  if (open) {
    return (
      <div className="w-full rounded-sm border border-stone-200 bg-brand-parchment/40 p-3">
        <textarea
          data-testid={testid ? `${testid}-note` : undefined}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Add a note for your coach (optional)…"
          className="w-full resize-y rounded-sm border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
        />
        <div className="mt-2 flex items-center gap-2">
          <button data-testid={testid ? `${testid}-confirm` : undefined} onClick={doShare} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-sm bg-brand-primary px-3 py-1.5 text-xs text-white hover:bg-brand-hover transition-colors disabled:opacity-60">
            <Share2 size={13} /> {busy ? "Sharing…" : "Share with coach"}
          </button>
          <button onClick={() => { setOpen(false); setNote(""); }} className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-stone-700 transition-colors">
            <X size={13} /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      data-testid={testid}
      type="button"
      onClick={() => setOpen(true)}
      disabled={!hasContent}
      title={hasContent ? "Share this with your coach" : "Add something first"}
      className="inline-flex items-center gap-1.5 rounded-sm border border-stone-300 px-3 py-2 text-sm text-stone-600 transition-all duration-300 hover:border-brand-primary hover:text-brand-primary disabled:opacity-40 disabled:hover:border-stone-300 disabled:hover:text-stone-600"
    >
      <Share2 size={14} /> Share with coach
    </button>
  );
}
